import { supabase } from '@/integrations/supabase/client';
import { Database, Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';
import {
  AsistenciaStatus,
  Event,
  Guest,
  EventProvider,
  EventTask,
  EventSchedule,
  GuestStatus,
  TaskProvider,
} from '@/types';

// Type mappings between our domain types and Supabase types
type EventRow = Tables<'eventos'>;
type EventInsert = TablesInsert<'eventos'>;
type EventUpdate = TablesUpdate<'eventos'>;

// The generated `Database` type predates the 20260514180000_mesas_seating
// migration, which added `numero_acompanantes`, `asistencia`, `checked_in_at`
// and `mesa_id` to invitados. Extend locally until `supabase gen types
// typescript` is re-run. Same pattern as plans / subscriptions in
// useSubscription.tsx.
type GuestRowBase = Tables<'invitados'>;
type GuestRow = GuestRowBase & {
  numero_acompanantes?: number;
  asistencia?: AsistenciaStatus;
  checked_in_at?: string | null;
  mesa_id?: string | null;
};
type GuestInsertBase = TablesInsert<'invitados'>;
type GuestInsert = GuestInsertBase & {
  numero_acompanantes?: number;
  asistencia?: AsistenciaStatus;
  checked_in_at?: string | null;
  mesa_id?: string | null;
};
type GuestUpdateBase = TablesUpdate<'invitados'>;
type GuestUpdate = GuestUpdateBase & {
  numero_acompanantes?: number;
  asistencia?: AsistenciaStatus;
  checked_in_at?: string | null;
  mesa_id?: string | null;
};

type EventProviderRow = Tables<'evento_proveedores'>;
type EventProviderInsert = TablesInsert<'evento_proveedores'>;

type EventTaskRow = Tables<'tareas_evento'>;
type EventTaskInsert = TablesInsert<'tareas_evento'>;
type EventTaskUpdate = TablesUpdate<'tareas_evento'>;

type EventScheduleRow = Tables<'cronograma_evento'>;
type EventScheduleInsert = TablesInsert<'cronograma_evento'>;
type EventScheduleUpdate = TablesUpdate<'cronograma_evento'>;

// Add new type definitions for guest messages
type GuestMessageRow = Tables<'guest_messages'>;
type GuestMessageInsert = TablesInsert<'guest_messages'>;

interface GuestMessage {
  id: string;
  guest_id: string;
  twilio_sid: string;
  cuerpo: string;
  tipo: 'INCOMING' | 'OUTGOING';
  incluye_botones?: boolean;
  created_at: Date;
  updated_at: Date;
}

// Helper functions to convert between domain types and database types
const eventRowToEvent = (row: EventRow): Event => ({
  id: row.id,
  workspace_id: row.workspace_id,
  nombre: row.nombre,
  fecha: new Date(row.fecha),
  contacto1_nombre: row.contacto1_nombre,
  contacto1_tel: row.contacto1_tel,
  contacto1_email: row.contacto1_email,
  contacto2_nombre: row.contacto2_nombre,
  contacto2_tel: row.contacto2_tel,
  contacto2_email: row.contacto2_email,
  status: row.status,
  created_at: new Date(row.created_at),
  whatsapp_template: row.whatsapp_template
});

const eventToEventInsert = (event: Omit<Event, 'id' | 'created_at' | 'workspace_id'>, workspaceId: string): EventInsert => ({
  workspace_id: workspaceId,
  nombre: event.nombre,
  fecha: event.fecha.toISOString().split('T')[0],
  contacto1_nombre: event.contacto1_nombre,
  contacto1_tel: event.contacto1_tel,
  contacto1_email: event.contacto1_email,
  contacto2_nombre: event.contacto2_nombre,
  contacto2_tel: event.contacto2_tel,
  contacto2_email: event.contacto2_email,
  status: event.status
});

const guestRowToGuest = (row: GuestRow): Guest => ({
  id: row.id,
  event_id: row.evento_id,
  nombre: row.nombre,
  tel: row.tel,
  estado: row.estado,
  fecha_respuesta: row.fecha_respuesta ? new Date(row.fecha_respuesta) : undefined,
  numero_acompanantes: row.numero_acompanantes ?? 0,
  asistencia: row.asistencia ?? 'PENDIENTE',
  checked_in_at: row.checked_in_at ? new Date(row.checked_in_at) : undefined,
  mesa_id: row.mesa_id ?? null,
});

const guestToGuestInsert = (
  guest: Omit<Guest, 'id'> & Partial<Pick<Guest, 'numero_acompanantes' | 'asistencia' | 'mesa_id'>>,
  eventId: string,
): GuestInsert => ({
  evento_id: eventId,
  nombre: guest.nombre,
  tel: guest.tel,
  estado: guest.estado,
  fecha_respuesta: guest.fecha_respuesta?.toISOString(),
  numero_acompanantes: guest.numero_acompanantes ?? 0,
  asistencia: guest.asistencia ?? 'PENDIENTE',
  mesa_id: guest.mesa_id ?? null,
});

const guestMessageRowToGuestMessage = (row: GuestMessageRow): GuestMessage => ({
  id: row.id,
  guest_id: row.guest_id,
  twilio_sid: row.twilio_sid,
  cuerpo: row.cuerpo,
  tipo: row.tipo as 'INCOMING' | 'OUTGOING',
  incluye_botones: row.incluye_botones || false,
  created_at: new Date(row.created_at),
  updated_at: new Date(row.updated_at)
});

// Get current user's workspace ID
const getCurrentWorkspaceId = async (): Promise<string> => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error || !session?.user?.id) {
    throw new Error('Usuario no autenticado');
  }
  return session.user.id; // workspace_id is the same as user_id in our schema
};

export const eventService = {
  // Events CRUD
  getEvents: async (): Promise<Event[]> => {
    const { data, error } = await supabase
      .from('eventos')
      .select('*')
      .order('fecha', { ascending: true });
    
    if (error) throw error;
    return data.map(eventRowToEvent);
  },

  getEvent: async (id: string): Promise<Event | null> => {
    const { data, error } = await supabase
      .from('eventos')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null; // No rows returned
      throw error;
    }
    
    return eventRowToEvent(data);
  },

  createEvent: async (event: Omit<Event, 'id' | 'created_at' | 'workspace_id'>): Promise<Event> => {
    const workspaceId = await getCurrentWorkspaceId();
    const eventInsert = eventToEventInsert(event, workspaceId);
    
    const { data, error } = await supabase
      .from('eventos')
      .insert(eventInsert)
      .select()
      .single();
    
    if (error) throw error;
    return eventRowToEvent(data);
  },

  updateEvent: async (id: string, event: Partial<Omit<Event, 'id' | 'created_at' | 'workspace_id'>>): Promise<Event> => {
    const eventUpdate: EventUpdate = {
      ...event,
      fecha: event.fecha?.toISOString().split('T')[0],
      updated_at: new Date().toISOString()
    };
    
    const { data, error } = await supabase
      .from('eventos')
      .update(eventUpdate)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return eventRowToEvent(data);
  },

  deleteEvent: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from('eventos')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  // Guests CRUD
  getEventGuests: async (eventId: string): Promise<Guest[]> => {
    const { data, error } = await supabase
      .from('invitados')
      .select('*')
      .eq('evento_id', eventId)
      .order('nombre', { ascending: true });
    
    if (error) throw error;
    return data.map((row) => guestRowToGuest(row as GuestRow));
  },

  createGuest: async (guest: Omit<Guest, 'id' | 'event_id'>, eventId: string): Promise<Guest> => {
    const guestWithEventId = { ...guest, event_id: eventId };
    const guestInsert = guestToGuestInsert(guestWithEventId, eventId);

    const { data, error } = await supabase
      .from('invitados')
      .insert(guestInsert as TablesInsert<'invitados'>)
      .select()
      .single();

    if (error) throw error;
    return guestRowToGuest(data as GuestRow);
  },

  /**
   * Bulk-insert multiple guests for a single event. Used by the CSV import flow.
   * Inserts run in chunks of `chunkSize` (default 50) so the UI can show
   * determinate progress and so one bad batch can't bring down the whole import.
   * If any chunk fails, already-inserted chunks remain in the DB and the error
   * is thrown after `onProgress` is called one last time with the final count.
   *
   * Returns the inserted rows so the caller can merge them into local state
   * without an extra round-trip.
   */
  bulkCreateGuests: async (
    guests: Array<Omit<Guest, 'id' | 'event_id'>>,
    eventId: string,
    options?: {
      chunkSize?: number;
      onProgress?: (inserted: number, total: number) => void;
    },
  ): Promise<Guest[]> => {
    if (guests.length === 0) return [];

    const chunkSize = Math.max(1, options?.chunkSize ?? 50);
    const onProgress = options?.onProgress;
    const total = guests.length;
    const inserted: Guest[] = [];

    // Emit a starting tick so the UI can render 0/total before the first
    // network round-trip completes.
    onProgress?.(0, total);

    for (let i = 0; i < guests.length; i += chunkSize) {
      const slice = guests.slice(i, i + chunkSize);
      const inserts: GuestInsert[] = slice.map((g) =>
        guestToGuestInsert({ ...g, event_id: eventId }, eventId),
      );

      const { data, error } = await supabase
        .from('invitados')
        .insert(inserts as TablesInsert<'invitados'>[])
        .select();

      if (error) {
        onProgress?.(inserted.length, total);
        throw error;
      }

      for (const row of data ?? []) inserted.push(guestRowToGuest(row as GuestRow));
      onProgress?.(inserted.length, total);
    }

    return inserted;
  },

  updateGuest: async (id: string, guest: Partial<Omit<Guest, 'id' | 'event_id'>>): Promise<Guest> => {
    const { fecha_respuesta, checked_in_at, ...rest } = guest;
    const guestUpdate: GuestUpdate = {
      ...rest,
      fecha_respuesta: fecha_respuesta?.toISOString(),
      checked_in_at: checked_in_at ? checked_in_at.toISOString() : checked_in_at === null ? null : undefined,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('invitados')
      .update(guestUpdate as TablesUpdate<'invitados'>)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return guestRowToGuest(data as GuestRow);
  },

  // ---- Seating & day-of attendance (Mesas feature) ------------------------

  /**
   * Assign or clear a guest's mesa. Pass `null` to unassign.
   * `mesa_id` is on a column added in 20260514180000_mesas_seating.sql; the
   * generated Supabase types don't know about it yet, so we cast through
   * `TablesUpdate<'invitados'>` after augmenting our local `GuestUpdate`.
   */
  assignGuestToMesa: async (guestId: string, mesaId: string | null): Promise<Guest> => {
    const update: GuestUpdate = {
      mesa_id: mesaId,
      updated_at: new Date().toISOString(),
    };
    const { data, error } = await supabase
      .from('invitados')
      .update(update as TablesUpdate<'invitados'>)
      .eq('id', guestId)
      .select()
      .single();
    if (error) throw error;
    return guestRowToGuest(data as GuestRow);
  },

  /**
   * Assign many guests at once. Fires N updates in parallel; Supabase doesn't
   * give us a true bulk-update API for heterogeneous values. Caller is
   * expected to be small (a single drag = 1 update; rare batch operations
   * stay under 50).
   */
  bulkAssignGuests: async (
    updates: Array<{ guestId: string; mesaId: string | null }>,
  ): Promise<void> => {
    if (updates.length === 0) return;
    const nowIso = new Date().toISOString();
    const results = await Promise.all(
      updates.map(({ guestId, mesaId }) => {
        const patch: GuestUpdate = { mesa_id: mesaId, updated_at: nowIso };
        return supabase
          .from('invitados')
          .update(patch as TablesUpdate<'invitados'>)
          .eq('id', guestId);
      }),
    );
    const firstError = results.find((r) => r.error)?.error;
    if (firstError) throw firstError;
  },

  /**
   * Update day-of attendance status. When marking PRESENTE we also set
   * `checked_in_at` so the planner can later see arrival times.
   */
  setGuestAttendance: async (
    guestId: string,
    asistencia: AsistenciaStatus,
  ): Promise<Guest> => {
    const update: GuestUpdate = {
      asistencia,
      checked_in_at: asistencia === 'PRESENTE' ? new Date().toISOString() : null,
      updated_at: new Date().toISOString(),
    };
    const { data, error } = await supabase
      .from('invitados')
      .update(update as TablesUpdate<'invitados'>)
      .eq('id', guestId)
      .select()
      .single();
    if (error) throw error;
    return guestRowToGuest(data as GuestRow);
  },

  deleteGuest: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from('invitados')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  // Event Providers
  getEventProviders: async (eventId: string): Promise<EventProvider[]> => {
    const { data, error } = await supabase
      .from('evento_proveedores')
      .select(`
        *,
        proveedores:proveedor_id (
          id,
          nombre,
          email,
          tel,
          tipos_proveedores:tipo_id (
            nombre
          )
        )
      `)
      .eq('evento_id', eventId);
    
    if (error) throw error;
    
    type EventoProveedorRow = {
      id: string;
      evento_id: string;
      proveedor_id: string;
      descripcion_servicio: string;
      precio: string | number;
      pagado: string | number;
      proveedores: {
        id: string;
        nombre: string;
        email: string | null;
        tel: string | null;
        tipos_proveedores: { nombre: string } | null;
      };
    };

    return (data as unknown as EventoProveedorRow[]).map((row): EventProvider => ({
      id: row.id,
      event_id: row.evento_id,
      provider_id: row.proveedor_id,
      descripcion_servicio: row.descripcion_servicio,
      precio: parseFloat(String(row.precio)),
      pagado: parseFloat(String(row.pagado)),
      provider: {
        id: row.proveedores.id,
        nombre: row.proveedores.nombre,
        email: row.proveedores.email ?? undefined,
        tel: row.proveedores.tel ?? undefined,
        tipo: row.proveedores.tipos_proveedores?.nombre ?? ''
      }
    }));
  },

  assignProviderToEvent: async (eventId: string, providerId: string, descripcion: string, precio: number): Promise<void> => {
    const { error } = await supabase
      .from('evento_proveedores')
      .insert({
        evento_id: eventId,
        proveedor_id: providerId,
        descripcion_servicio: descripcion,
        precio,
        pagado: 0
      });
    
    if (error) throw error;
  },

  updateEventProvider: async (id: string, updates: { descripcion_servicio?: string; precio?: number; pagado?: number }): Promise<void> => {
    const { error } = await supabase
      .from('evento_proveedores')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', id);
    
    if (error) throw error;
  },

  // Event Tasks
  //
  // `getEventTasks` joins through `tarea_proveedores` so each task carries
  // its linked vendor list inline. The nested-select pattern mirrors
  // `getEventProviders` (lines ~280-330 in this file). The shape returned by
  // PostgREST is hand-typed here because `tarea_proveedores` predates the
  // last `supabase gen types typescript` run.
  getEventTasks: async (eventId: string): Promise<EventTask[]> => {
    const { data, error } = await supabase
      .from('tareas_evento')
      .select(`
        *,
        tarea_proveedores (
          evento_proveedor:evento_proveedor_id (
            id,
            descripcion_servicio,
            proveedores:proveedor_id (
              id, nombre, email, tel,
              tipos_proveedores:tipo_id ( nombre )
            )
          )
        )
      `)
      .eq('evento_id', eventId)
      .order('inicio', { ascending: true });

    if (error) throw error;

    type JoinedRow = EventTaskRow & {
      tarea_proveedores?: Array<{
        evento_proveedor: {
          id: string;
          descripcion_servicio: string | null;
          proveedores: {
            id: string;
            nombre: string;
            email: string | null;
            tel: string | null;
            tipos_proveedores: { nombre: string } | null;
          } | null;
        } | null;
      }>;
    };

    return (data as unknown as JoinedRow[]).map((row): EventTask => ({
      id: row.id,
      event_id: row.evento_id,
      titulo: row.titulo,
      inicio: new Date(row.inicio),
      fin: new Date(row.fin),
      notas: row.notas,
      providers: (row.tarea_proveedores ?? [])
        .map((tp) => tp.evento_proveedor)
        .filter((ep): ep is NonNullable<typeof ep> => ep !== null && ep.proveedores !== null)
        .map((ep): TaskProvider => ({
          evento_proveedor_id: ep.id,
          provider_id: ep.proveedores!.id,
          nombre: ep.proveedores!.nombre,
          tipo: ep.proveedores!.tipos_proveedores?.nombre ?? '',
          email: ep.proveedores!.email ?? undefined,
          tel: ep.proveedores!.tel ?? undefined,
          descripcion_servicio: ep.descripcion_servicio ?? undefined,
        })),
    }));
  },

  /**
   * Fetch a single task with its providers — used by TaskDetailModal after
   * an edit to refresh the view without a full list refetch.
   */
  getEventTask: async (taskId: string): Promise<EventTask | null> => {
    const { data, error } = await supabase
      .from('tareas_evento')
      .select(`
        *,
        tarea_proveedores (
          evento_proveedor:evento_proveedor_id (
            id,
            descripcion_servicio,
            proveedores:proveedor_id (
              id, nombre, email, tel,
              tipos_proveedores:tipo_id ( nombre )
            )
          )
        )
      `)
      .eq('id', taskId)
      .maybeSingle();
    if (error) throw error;
    if (!data) return null;

    type JoinedRow = EventTaskRow & {
      tarea_proveedores?: Array<{
        evento_proveedor: {
          id: string;
          descripcion_servicio: string | null;
          proveedores: {
            id: string;
            nombre: string;
            email: string | null;
            tel: string | null;
            tipos_proveedores: { nombre: string } | null;
          } | null;
        } | null;
      }>;
    };
    const row = data as unknown as JoinedRow;
    return {
      id: row.id,
      event_id: row.evento_id,
      titulo: row.titulo,
      inicio: new Date(row.inicio),
      fin: new Date(row.fin),
      notas: row.notas,
      providers: (row.tarea_proveedores ?? [])
        .map((tp) => tp.evento_proveedor)
        .filter((ep): ep is NonNullable<typeof ep> => ep !== null && ep.proveedores !== null)
        .map((ep): TaskProvider => ({
          evento_proveedor_id: ep.id,
          provider_id: ep.proveedores!.id,
          nombre: ep.proveedores!.nombre,
          tipo: ep.proveedores!.tipos_proveedores?.nombre ?? '',
          email: ep.proveedores!.email ?? undefined,
          tel: ep.proveedores!.tel ?? undefined,
          descripcion_servicio: ep.descripcion_servicio ?? undefined,
        })),
    };
  },

  /**
   * Reconcile the task→provider links to the given set. Diff-based so we
   * only insert what's new and delete what's removed; rows that are already
   * present stay untouched. The `tarea_proveedores` table predates the
   * latest type regeneration so we cast through `unknown` here.
   */
  setTaskProviders: async (
    taskId: string,
    eventoProveedorIds: string[],
  ): Promise<void> => {
    const client = supabase as unknown as {
      from: (t: string) => {
        select: (cols: string) => {
          eq: (col: string, value: unknown) => Promise<{
            data: Array<{ id: string; evento_proveedor_id: string }> | null;
            error: { message?: string } | null;
          }>;
        };
        insert: (rows: Array<{ tarea_id: string; evento_proveedor_id: string }>) => Promise<{
          error: { message?: string } | null;
        }>;
        delete: () => {
          in: (col: string, values: string[]) => Promise<{
            error: { message?: string } | null;
          }>;
        };
      };
    };

    const { data: existing, error: readErr } = await client
      .from('tarea_proveedores')
      .select('id, evento_proveedor_id')
      .eq('tarea_id', taskId);
    if (readErr) throw new Error(readErr.message ?? 'Error leyendo proveedores');

    const want = new Set(eventoProveedorIds);
    const have = new Set((existing ?? []).map((r) => r.evento_proveedor_id));

    const toAdd = [...want].filter((id) => !have.has(id));
    const toRemove = (existing ?? [])
      .filter((r) => !want.has(r.evento_proveedor_id))
      .map((r) => r.id);

    if (toAdd.length > 0) {
      const { error } = await client.from('tarea_proveedores').insert(
        toAdd.map((evento_proveedor_id) => ({
          tarea_id: taskId,
          evento_proveedor_id,
        })),
      );
      if (error) throw new Error(error.message ?? 'Error agregando proveedores');
    }
    if (toRemove.length > 0) {
      const { error } = await client
        .from('tarea_proveedores')
        .delete()
        .in('id', toRemove);
      if (error) throw new Error(error.message ?? 'Error quitando proveedores');
    }
  },

  createEventTask: async (task: Omit<EventTask, 'id'>): Promise<EventTask> => {
    const taskInsert: EventTaskInsert = {
      evento_id: task.event_id,
      titulo: task.titulo,
      inicio: task.inicio.toISOString(),
      fin: task.fin.toISOString(),
      notas: task.notas
    };

    const { data, error } = await supabase
      .from('tareas_evento')
      .insert(taskInsert)
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      event_id: data.evento_id,
      titulo: data.titulo,
      inicio: new Date(data.inicio),
      fin: new Date(data.fin),
      notas: data.notas
    };
  },

  updateEventTask: async (id: string, task: Partial<Omit<EventTask, 'id' | 'event_id'>>): Promise<EventTask> => {
    const taskUpdate: EventTaskUpdate = {
      ...task,
      inicio: task.inicio?.toISOString(),
      fin: task.fin?.toISOString(),
      updated_at: new Date().toISOString()
    };
    
    const { data, error } = await supabase
      .from('tareas_evento')
      .update(taskUpdate)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    
    return {
      id: data.id,
      event_id: data.evento_id,
      titulo: data.titulo,
      inicio: new Date(data.inicio),
      fin: new Date(data.fin),
      notas: data.notas
    };
  },

  deleteEventTask: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from('tareas_evento')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  // Event Schedule
  getEventSchedule: async (eventId: string): Promise<EventSchedule[]> => {
    const { data, error } = await supabase
      .from('cronograma_evento')
      .select('*')
      .eq('evento_id', eventId)
      .order('orden', { ascending: true });
    
    if (error) throw error;
    
    return data.map((row: EventScheduleRow): EventSchedule => ({
      id: row.id,
      event_id: row.evento_id,
      hora: row.hora,
      actividad: row.actividad,
      orden: row.orden
    }));
  },

  createEventScheduleItem: async (item: Omit<EventSchedule, 'id'>): Promise<EventSchedule> => {
    const scheduleInsert: EventScheduleInsert = {
      evento_id: item.event_id,
      hora: item.hora,
      actividad: item.actividad,
      orden: item.orden
    };
    
    const { data, error } = await supabase
      .from('cronograma_evento')
      .insert(scheduleInsert)
      .select()
      .single();
    
    if (error) throw error;
    
    return {
      id: data.id,
      event_id: data.evento_id,
      hora: data.hora,
      actividad: data.actividad,
      orden: data.orden
    };
  },

  updateEventScheduleItem: async (id: string, item: Partial<Omit<EventSchedule, 'id' | 'event_id'>>): Promise<EventSchedule> => {
    const scheduleUpdate: EventScheduleUpdate = {
      ...item,
      updated_at: new Date().toISOString()
    };
    
    const { data, error } = await supabase
      .from('cronograma_evento')
      .update(scheduleUpdate)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    
    return {
      id: data.id,
      event_id: data.evento_id,
      hora: data.hora,
      actividad: data.actividad,
      orden: data.orden
    };
  },

  deleteEventScheduleItem: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from('cronograma_evento')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  // Guest messages
  getGuestMessages: async (guestId: string): Promise<GuestMessage[]> => {
    // Get the current session token
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session?.access_token) {
      throw new Error('Usuario no autenticado');
    }

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/getGuestMessages?guestId=${guestId}`,
      {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error fetching messages');
    }

    const data = await response.json();
    return data.map(guestMessageRowToGuestMessage);
  },

  sendGuestMessage: async (guestId: string, eventId: string, message: string, includeButtons: boolean = false): Promise<void> => {
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sendInvites`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({
        guestIds: [guestId],
        eventId,
        message,
        includeButtons
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error sending message');
    }
  }
};

